import { Theme, useTheme } from '@mui/material/styles';
import React, { useEffect, useRef } from 'react';
import seedrandom from 'seedrandom';
import * as THREE from 'three';

import { sceneSignals } from './sceneSignals';
import {
    MAP_SCALE,
    MapPoint,
    MAX_MAP_POINTS,
    neighbourPairs,
    placeholderPoints,
} from './vectorMap';

const SEED = 'VictorIA';
// Nodes drawn while the memory map is empty or not loaded yet.
const PLACEHOLDER_COUNT = 20;
const PARTICLE_COUNT = 15000;
const SPHERE_RADIUS = 15;
// Fog ends this far past the particle field's edge.
const FOG_MARGIN = 35;
const BASE_FOV = 75;
const CAMERA_DISTANCE = 5;
const LOOK_INTENSITY = 1.5;
const NODE_RADIUS = 0.02;
const LINK_OPACITY = 0.45;

interface ScenePalette {
    background: THREE.Color;
    primary: THREE.Color;
    secondary: THREE.Color;
    star: THREE.Color;
}

const buildPalette = (theme: Theme): ScenePalette => {
    const light = theme.palette.mode === 'light';
    const primary = new THREE.Color(theme.palette.primary.main);
    const star = new THREE.Color(theme.palette.primary.main);
    if (light) {
        // Vivid, mid-tone ink on paper rather than a pale tint.
        const hsl = { h: 0, s: 0, l: 0 };
        star.getHSL(hsl);
        star.setHSL(hsl.h, 1, 0.5);
    }
    return {
        background: new THREE.Color(theme.palette.background.default),
        primary,
        secondary: new THREE.Color(theme.palette.secondary.main),
        star,
    };
};

const PARTICLE_VERTEX_SHADER = /* glsl */ `
    uniform float uSize;
    uniform float uPixelRatio;
    uniform float uSphereRadius;

    void main() {
        vec3 displaced = normalize(position) * uSphereRadius;
        vec4 mvPosition = modelViewMatrix * vec4(displaced, 1.0);
        gl_PointSize = uSize * uPixelRatio * (uSphereRadius / -mvPosition.z);
        gl_Position = projectionMatrix * mvPosition;
    }
`;

const PARTICLE_FRAGMENT_SHADER = /* glsl */ `
    uniform vec3 uColor;

    void main() {
        float d = length(gl_PointCoord - vec2(0.5));
        float alpha = 1.0 - smoothstep(0.0, 0.5, d);
        if (alpha < 0.01) discard;
        gl_FragColor = vec4(uColor, alpha);
    }
`;

const createRenderer = (): THREE.WebGLRenderer | null => {
    try {
        return new THREE.WebGLRenderer({ antialias: true });
    } catch {
        return null;
    }
};

/** Decorative background: the memory map as nodes and links inside a star field. */
export const ChatScene: React.FC = () => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const theme = useTheme();
    const paletteRef = useRef<ScenePalette>(buildPalette(theme));

    useEffect(() => {
        paletteRef.current = buildPalette(theme);
    }, [theme]);

    useEffect(() => {
        const container = containerRef.current;
        const renderer = container ? createRenderer() : null;
        if (!container || !renderer) {
            return;
        }

        const reducedMotion = window.matchMedia?.(
            '(prefers-reduced-motion: reduce)'
        )?.matches;
        const motion = reducedMotion ? 0.25 : 1;

        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.domElement.style.display = 'block';
        container.appendChild(renderer.domElement);

        const palette = paletteRef.current;
        const scene = new THREE.Scene();
        scene.background = palette.background.clone();
        scene.fog = new THREE.Fog(palette.background.getHex(), 0, 50);

        const camera = new THREE.PerspectiveCamera(
            BASE_FOV,
            window.innerWidth / window.innerHeight,
            0.1,
            1000
        );
        camera.position.set(0, 0, CAMERA_DISTANCE);

        const field = new THREE.Group();
        scene.add(field);

        // The map is loaded once, so nodes and links are built once per map
        // and never touched per frame.
        const nodeGeometry = new THREE.SphereGeometry(NODE_RADIUS, 12, 12);
        const nodeMaterial = new THREE.MeshBasicMaterial({
            color: palette.primary.clone(),
        });
        let nodes: THREE.InstancedMesh | null = null;
        let links: THREE.LineSegments | null = null;
        let sphereRadius = SPHERE_RADIUS;

        const disposeMap = () => {
            if (nodes) {
                field.remove(nodes);
                nodes.dispose();
                nodes = null;
            }
            if (links) {
                field.remove(links);
                links.geometry.dispose();
                (links.material as THREE.Material).dispose();
                links = null;
            }
        };

        const applyMap = (mapPoints: MapPoint[]) => {
            disposeMap();
            const source =
                mapPoints.length > 0
                    ? mapPoints.slice(-MAX_MAP_POINTS)
                    : placeholderPoints(
                          seedrandom(`${SEED}-placeholder`),
                          PLACEHOLDER_COUNT
                      );
            const positions = source.map(
                (point) =>
                    new THREE.Vector3(
                        point.x * MAP_SCALE,
                        point.y * MAP_SCALE,
                        point.z * MAP_SCALE
                    )
            );

            // The star field reaches the furthest point of the map.
            sphereRadius = positions.reduce(
                (max, position) => Math.max(max, position.length()),
                SPHERE_RADIUS
            );
            (scene.fog as THREE.Fog).far = sphereRadius + FOG_MARGIN;
            particleUniforms.uSphereRadius.value = sphereRadius;

            nodes = new THREE.InstancedMesh(
                nodeGeometry,
                nodeMaterial,
                positions.length
            );
            const matrix = new THREE.Matrix4();
            positions.forEach((position, i) => {
                nodes!.setMatrixAt(i, matrix.setPosition(position));
            });
            field.add(nodes);

            const pairs = neighbourPairs(source);
            if (pairs.length > 0) {
                const linkPositions = new Float32Array(pairs.length * 6);
                pairs.forEach(([a, b], i) => {
                    positions[a].toArray(linkPositions, i * 6);
                    positions[b].toArray(linkPositions, i * 6 + 3);
                });
                const geometry = new THREE.BufferGeometry();
                geometry.setAttribute(
                    'position',
                    new THREE.BufferAttribute(linkPositions, 3)
                );
                const material = new THREE.LineBasicMaterial({
                    color: palette.primary.clone().lerp(palette.secondary, 0.5),
                    transparent: true,
                    opacity: LINK_OPACITY,
                    depthWrite: false,
                });
                links = new THREE.LineSegments(geometry, material);
                links.frustumCulled = false;
                field.add(links);
            }
        };

        const random = seedrandom(SEED);
        const particlePositions = new Float32Array(PARTICLE_COUNT * 3);
        for (let i = 0; i < PARTICLE_COUNT; i++) {
            const theta = random() * Math.PI * 2;
            const phi = Math.acos(2 * random() - 1);
            particlePositions[i * 3] = Math.sin(phi) * Math.cos(theta);
            particlePositions[i * 3 + 1] = Math.sin(phi) * Math.sin(theta);
            particlePositions[i * 3 + 2] = Math.cos(phi);
        }
        const particleGeometry = new THREE.BufferGeometry();
        particleGeometry.setAttribute(
            'position',
            new THREE.Float32BufferAttribute(particlePositions, 3)
        );
        const particleUniforms = {
            uSize: { value: 2 },
            uPixelRatio: { value: renderer.getPixelRatio() },
            uSphereRadius: { value: SPHERE_RADIUS },
            uColor: { value: palette.star.clone() },
        };
        const particleMaterial = new THREE.ShaderMaterial({
            uniforms: particleUniforms,
            vertexShader: PARTICLE_VERTEX_SHADER,
            fragmentShader: PARTICLE_FRAGMENT_SHADER,
            transparent: true,
            depthWrite: false,
        });
        const particles = new THREE.Points(particleGeometry, particleMaterial);
        scene.add(particles);

        applyMap(sceneSignals.getSnapshot().vectorMap);
        const unsubscribe = sceneSignals.subscribe((event) => {
            if (event.type === 'map') {
                applyMap(event.points);
            }
        });

        const pointer = { x: 0, y: 0 };
        const handlePointerMove = (event: PointerEvent) => {
            pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
            pointer.y = (event.clientY / window.innerHeight) * 2 - 1;
        };
        const handleResize = () => {
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        };
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('resize', handleResize);

        const cameraTarget = new THREE.Vector3();
        let frameId = 0;
        let lastTime: number | null = null;

        const animate = (now: number) => {
            frameId = requestAnimationFrame(animate);
            const dt = Math.min(lastTime === null ? 0 : (now - lastTime) / 1000, 0.1);
            lastTime = now;

            const colors = paletteRef.current;
            (scene.background as THREE.Color).copy(colors.background);
            (scene.fog as THREE.Fog).color.copy(colors.background);
            nodeMaterial.color.copy(colors.primary);
            particleUniforms.uColor.value.copy(colors.star);
            if (links) {
                (links.material as THREE.LineBasicMaterial).color
                    .copy(colors.primary)
                    .lerp(colors.secondary, 0.5);
            }

            field.rotation.y += dt * motion * 0.015;
            particles.rotation.y += dt * motion * 0.01;
            particles.rotation.x += dt * motion * 0.004;

            cameraTarget.set(
                pointer.x * LOOK_INTENSITY * motion,
                -pointer.y * LOOK_INTENSITY * motion,
                CAMERA_DISTANCE
            );
            camera.position.lerp(cameraTarget, 1 - Math.exp(-3 * dt));
            camera.lookAt(scene.position);

            renderer.render(scene, camera);
        };
        frameId = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(frameId);
            unsubscribe();
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('resize', handleResize);
            disposeMap();
            nodeGeometry.dispose();
            nodeMaterial.dispose();
            particleGeometry.dispose();
            particleMaterial.dispose();
            renderer.dispose();
            renderer.domElement.remove();
        };
    }, []);

    return (
        <div
            ref={containerRef}
            aria-hidden="true"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: -1,
                pointerEvents: 'none',
            }}
        />
    );
};
